<template>
  <div>
    <div class="form-group">
      <Typeahead
        v-model="counterParty"
        label="Who is the Counterparty? (Business name)"
        :items="allAvailableCounterParties"
        :errors="$page.props.errors.counter_party_id"
        placeholder="Start typing to search..."
        :tolerance="0"
        return-field="id"
        id-field="id"
        search-field="name"
        display-field="name"
        @selected="counterPartySelected"
        @noResults="noCounterparty"
      >
        <template v-slot:item="slotProps">
          <span v-if="slotProps.match.name">
            {{ slotProps.match.name }}
            <br />
          </span>
          <strong v-if="slotProps.match.address"
            ><small>Address: {{ slotProps.match.address }}</small></strong
          >
          <strong v-if="slotProps.match.phone"
            ><small>Phone: {{ slotProps.match.phone }}</small></strong
          >
        </template>
        <template v-slot:noResults>
          <h6 class="mt-2">Can’t find your Counterparty?</h6>
          <small>
            <p class="mb-2">Are you sure there is a Framework Agreement for this Counterparty on Clausify? If not, please <span class="a">Create an Framework Agreement first</span>. Once the Framework Agreement is executed, you can configure a Framework Agreement SoW for that Counterparty. </p>
          </small>
        </template>
      </Typeahead>
      <div class="row">
        <small class="form-text form-helper col-md-9 mt-2">
          Can’t find the Counterparty you’re looking for? <InertiaLink :href="route('asset.create', 'framework-agreement')">Click here</InertiaLink> to set up a Framework Agreement for the Counterparty. This is mandatory to set up any Contracts for a Counterparty.
        </small>
      </div>
    </div>

    <div v-if="form.counter_party_id">
      <div class="form-group">
        <Choice
          v-model="form.role"
          @selected="frameworkAgreementSelected"
          :errors="$page.props.errors.role"
          label="What is your organisation's role?"
          :items="frameworkAgreementChoices"
        />
      </div>
    </div>

    <div v-if="form.counter_party_id && form.role">

      <div class="form-group">
        <Choice
          v-model="form.personal_data"
          @selected="$page.props.errors.personal_data = []"
          :errors="$page.props.errors.personal_data"
          label="Will the Supplier be required to Process Personal Data under this Contract?"
          :items="personalDataItems"
          :grow="false"
        />
      </div>

      <div class="form-group">
        <Choice
          v-model="form.acceptance"
          @selected="$page.props.errors.acceptance = []"
          :errors="$page.props.errors.acceptance"
          label="Will Acceptance apply to any of the Products delivered under this Contract?"
          :items="acceptanceItems"
          :grow="false"
        />
      </div>

      <div class="form-group">
        <Choice
          v-model="form.ipr"
          @selected="$page.props.errors.ipr = []"
          :errors="$page.props.errors.ipr"
          label="How will Contract IPR be categorised under this Contract?"
          :items="irpItems"
        />
      </div>
    </div>
  </div>
</template>

<script>
import mixin from './mixin';

export default {
  mixins: [mixin],

  data() {
    return {
      counterPartiesData: this.counterParties,
      counterParty: null,
      form: {
        counter_party_id: this.asset ? this.asset.counter_party_id : null,
        counter_party_type: this.asset ? this.asset.counter_party_type : null,
        role: this.asset ? this.asset.role : null,
        asset_id: this.asset ? this.asset.asset_id : null,

        personal_data: this.asset ? this.asset.fields.personal_data : null,
        acceptance: this.asset ? this.asset.fields.acceptance : null,
        ipr: this.asset ? this.asset.fields.ipr : null,
      },
      pow: {
        name: '',
        description: '',
      },
      showCreate: true,
      adding: false,
      sending: false,
      personalDataItems: [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
      ],
      acceptanceItems: [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
      ],
      irpItems: [
        {
          label: 'Type A',
          value: 'A',
          helper:
            'Owned by Supplier with licence to Customer (see Clause 17.2.2. of the Framework Agreement)',
        },
        {
          label: 'Type B',
          value: 'B',
          helper:
            'Owned by Customer, with broad licence back to Supplier (see Clause 17.2.3 of the Framework Agreement)',
        },
        {
          label: 'Type C',
          value: 'C',
          helper:
            'Owned by Customer, with very narrow licence back to Supplier (see Clause 17.2.4 of the Framework Agreement)',
        },
      ],
      errors: {},
      supplierFrameworkAgreement: null,
      customerFrameworkAgreement: null,
    };
  },

  computed: {
    allAvailableCounterParties() {
      const modifiedOrganisations = this.organisations.map((org) => ({
        id: org.id,
        type: 'App\\Models\\Organisation',
        name: org.name,
      }));

      const modifiedCounterParties = this.counterPartiesData.map((cp) => ({
        id: cp.id,
        type: 'App\\Models\\CounterParty',
        name: cp.name,
        address: cp.address,
        phone: cp.phone,
      }));

      return [...modifiedOrganisations, ...modifiedCounterParties].filter(
        (org) => org.id,
      );
    },

    correctRoute() {
      return this.form.counter_party_type === 'App\\Models\\CounterParty' ? 'counter-party' : 'organisation';
    },

    frameworkAgreementChoices() {
      return [
        {
          label: 'Selling goods and/or services (Supplier)',
          value: this.supplierFrameworkAgreement ? 'supplier' : '',
        }, {
          label: 'Buying goods and/or services (Customer)',
          value: this.customerFrameworkAgreement ? 'customer' : '',
        },
      ];
    },
  },

  methods: {
    counterPartySelected(cp) {
      this.$page.props.errors.counter_party_id = null;

      if (cp) {
        this.form.counter_party_id = cp.id;
        this.form.counter_party_type = cp.type;

        this.fetchExecutedFrameworkAgreements();
      } else {
        this.form.counter_party_id = null;
        this.form.counter_party_type = null;
        this.form.role = null;
        this.form.asset_id = null;
        this.supplierFrameworkAgreement = null;
        this.customerFrameworkAgreement = null;
      }
    },

    frameworkAgreementSelected() {
      this.form.asset_id = this.form.role === 'supplier' ? this.supplierFrameworkAgreement.id : this.customerFrameworkAgreement.id;

      this.$page.props.errors.asset_id = [];
    },

    fetchExecutedFrameworkAgreements() {
      window.axios
        .get(this.route('counter-party.asset.index', [this.form.counter_party_id, 'framework-agreement']))
        .then((response) => {
          this.supplierFrameworkAgreement = response.data.length ? response.data.find((frameworkAgreement) => frameworkAgreement.role === 'supplier') : null;
          this.customerFrameworkAgreement = response.data.length ? response.data.find((frameworkAgreement) => frameworkAgreement.role === 'customer') : null;
        })
        .catch((error) => {
          throw new Error(error);
        });
    },

    create() {
      this.showCreate = true;
    },

    cancel() {
      this.showCreate = false;
      this.pow.name = '';
      this.pow.description = '';
    },

    noCounterparty() {
      this.$inertia.visit(this.route('asset.create', 'master-service-agreement'));
    },
  },
};
</script>
